import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bot,
  ClipboardList,
  Columns,
  List,
  MessageSquare,
  Plus,
  RefreshCw,
  UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { DemandasKanban } from '@/components/Demandas/DemandasKanban';
import { DemandaCreateModal } from '@/components/Demandas/DemandaCreateModal';
import { DemandasChatPanel } from '@/components/Demandas/DemandasChatPanel';
import { AtendimentoMediaBubble } from '@/components/Demandas/AtendimentoMediaBubble';
import { AtendimentoComposer } from '@/components/Demandas/AtendimentoComposer';
import { AtendimentoMessageMeta } from '@/components/Demandas/AtendimentoMessageMeta';
import { DemandaClienteResumoCard } from '@/components/Demandas/DemandaClienteResumoCard';
import {
  ChatMsg,
  Demanda,
  DemandaClienteResumo,
  DemandaStatus,
  DemandaTipo,
  STATUS_LABELS,
  STATUS_OPTS,
  TIPO_LABELS,
  TIPO_OPTS,
  statusVariant,
  tipoVariant,
} from '@/components/Demandas/demandaTypes';

type Tab = 'kanban' | 'lista' | 'chats';
type CompanyOpt = { id: string; name: string };

function totalUnread(cols: Array<{ items: Demanda[] }>) {
  return cols.reduce(
    (acc, c) =>
      acc +
      c.items.reduce((s, d) => s + (d.conversation?.operatorUnreadCount ?? 0), 0),
    0
  );
}

export default function DemandasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.get('tab') === 'chats' ? 'chats' : 'kanban'
  );
  const [companies, setCompanies] = useState<CompanyOpt[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [items, setItems] = useState<Demanda[]>([]);
  const [total, setTotal] = useState(0);
  const [columns, setColumns] = useState<Array<{ status: DemandaStatus; items: Demanda[] }>>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Demanda | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [clienteResumo, setClienteResumo] = useState<DemandaClienteResumo | null>(null);
  const [clienteResumoLoading, setClienteResumoLoading] = useState(false);
  const unreadRef = useRef(0);
  const deepLinkDone = useRef(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get('/companies');
        const list = (res.data?.data ?? res.data ?? []) as CompanyOpt[];
        setCompanies(Array.isArray(list) ? list : []);
      } catch {
        setCompanies([]);
      }
    })();
  }, []);

  const filterParams = useCallback(() => {
    return {
      ...(companyId ? { companyId } : {}),
      ...(tipo ? { tipo } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    };
  }, [companyId, tipo, q]);

  const loadBoard = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const res = await api.get('/rique-atendimento/demandas/board', {
        params: filterParams(),
      });
      const nextCols = (res.data?.data?.columns ?? []) as Array<{
        status: DemandaStatus;
        items: Demanda[];
      }>;
      const nextUnread = totalUnread(nextCols);
      if (unreadRef.current > 0 && nextUnread > unreadRef.current) {
        toast('Nova mensagem do solicitante', { icon: '💬' });
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification('Demandas', { body: 'Cliente respondeu no atendimento' });
          } else if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'default'
          ) {
            void Notification.requestPermission();
          }
        } catch {
          /* browser notify opcional */
        }
      }
      unreadRef.current = nextUnread;
      setColumns(nextCols);
    } catch (e: any) {
      if (!opts?.silent) toast.error(e?.response?.data?.message || 'Falha ao carregar kanban');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [filterParams]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rique-atendimento/demandas', {
        params: {
          ...filterParams(),
          ...(status ? { status } : {}),
          pageSize: 100,
        },
      });
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.total ?? 0);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao listar demandas');
    } finally {
      setLoading(false);
    }
  }, [filterParams, status]);

  const refresh = useCallback(async () => {
    if (tab === 'kanban') await loadBoard();
    else if (tab === 'lista') await loadList();
  }, [tab, loadBoard, loadList]);

  useEffect(() => {
    if (tab === 'kanban') void loadBoard();
    if (tab === 'lista') void loadList();
  }, [tab, loadBoard, loadList]);

  useEffect(() => {
    if (tab !== 'kanban') return;
    const t = setInterval(() => void loadBoard({ silent: true }), 15_000);
    return () => clearInterval(t);
  }, [tab, loadBoard]);

  const openDetail = async (d: Demanda) => {
    try {
      const res = await api.get(`/rique-atendimento/demandas/${d.id}`);
      const full = res.data?.data as Demanda;
      setSelected(full);
      setDetailOpen(true);
      setClienteResumo(null);
      if (full.solicitanteActorType === 'CLIENTE') {
        setClienteResumoLoading(true);
        void api
          .get(`/rique-atendimento/demandas/${full.id}/cliente-resumo`)
          .then((r) => setClienteResumo((r.data?.data ?? null) as DemandaClienteResumo | null))
          .catch(() => setClienteResumo(null))
          .finally(() => setClienteResumoLoading(false));
      } else {
        setClienteResumoLoading(false);
      }
      if (full.conversationId) {
        await api
          .post(`/rique-atendimento/portal/conversations/${full.conversationId}/mark-read`)
          .catch(() => undefined);
        const msgRes = await api.get(
          `/rique-atendimento/portal/conversations/${full.conversationId}/messages`
        );
        setMessages(msgRes.data?.data ?? []);
        void loadBoard({ silent: true });
      } else {
        setMessages([]);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao abrir demanda');
    }
  };

  useEffect(() => {
    if (deepLinkDone.current) return;
    const demandaId = searchParams.get('demandaId');
    const tabParam = searchParams.get('tab');
    if (tabParam === 'chats') setTab('chats');
    if (!demandaId) return;
    deepLinkDone.current = true;
    void (async () => {
      try {
        const res = await api.get(`/rique-atendimento/demandas/${demandaId}`);
        const full = res.data?.data as Demanda;
        await openDetail(full);
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.delete('demandaId');
            n.delete('conversationId');
            return n;
          },
          { replace: true }
        );
      } catch {
        toast.error('Demanda da notificação não encontrada');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const patch = async (id: string, body: Record<string, unknown>) => {
    try {
      const res = await api.patch(`/rique-atendimento/demandas/${id}`, body);
      const updated = res.data?.data as Demanda;
      setSelected((prev) => (prev?.id === id ? { ...prev, ...updated } : prev));
      toast.success('Demanda atualizada');
      await refresh();
      return updated;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao atualizar');
      throw e;
    }
  };

  const moveCard = async (id: string, newStatus: DemandaStatus) => {
    const prev = columns;
    setColumns((cols) => {
      let moved: Demanda | undefined;
      const stripped = cols.map((c) => {
        const found = c.items.find((i) => i.id === id);
        if (found) moved = found;
        return { ...c, items: c.items.filter((i) => i.id !== id) };
      });
      if (!moved) return cols;
      return stripped.map((c) =>
        c.status === newStatus ? { ...c, items: [{ ...moved!, status: newStatus }, ...c.items] } : c
      );
    });
    try {
      await api.patch(`/rique-atendimento/demandas/${id}`, { status: newStatus });
    } catch (e: any) {
      setColumns(prev);
      toast.error(e?.response?.data?.message || 'Falha ao mover card');
    }
  };

  const takeover = async () => {
    if (!selected?.conversationId) return;
    try {
      await api.post(
        `/rique-atendimento/portal/conversations/${selected.conversationId}/takeover`
      );
      toast.success('Você assumiu o atendimento');
      await openDetail(selected);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha no takeover');
    }
  };

  const release = async () => {
    if (!selected?.conversationId) return;
    try {
      await api.post(
        `/rique-atendimento/portal/conversations/${selected.conversationId}/release`
      );
      toast.success('Atendimento devolvido à IA');
      await openDetail(selected);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao liberar');
    }
  };

  const reloadMessages = async () => {
    if (!selected?.conversationId) return;
    const msgRes = await api.get(
      `/rique-atendimento/portal/conversations/${selected.conversationId}/messages`
    );
    setMessages(msgRes.data?.data ?? []);
  };

  useEffect(() => {
    if (!detailOpen || !selected?.conversationId) return;
    const t = setInterval(() => {
      void reloadMessages().catch(() => undefined);
    }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen, selected?.conversationId]);

  const tabBtn = (id: Tab, label: string, icon: ReactNode) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 ${
        tab === id
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Demandas
          </h1>
          <p className="text-sm text-muted-foreground">
            Kanban, tipos e acompanhamento dos chats de atendimento
          </p>
        </div>
        <div className="flex gap-2">
          {tab !== 'chats' ? (
            <Button variant="outline" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          ) : null}
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova demanda
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3">
          <div className="w-52">
            <Select value={companyId} onValueChange={setCompanyId}>
              <option value="">Todas as empresas</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-44">
            <Select value={tipo} onValueChange={setTipo}>
              <option value="">Todos os tipos</option>
              {TIPO_OPTS.map((t) => (
                <option key={t} value={t}>
                  {TIPO_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
          {tab === 'lista' ? (
            <div className="w-48">
              <Select value={status} onValueChange={setStatus}>
                <option value="">Todos os status</option>
                {STATUS_OPTS.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {tab !== 'chats' ? (
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar protocolo, título, solicitante…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void refresh()}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex gap-1 border-b">
        {tabBtn('kanban', 'Kanban', <Columns className="w-4 h-4" />)}
        {tabBtn('lista', 'Lista', <List className="w-4 h-4" />)}
        {tabBtn('chats', 'Chats', <MessageSquare className="w-4 h-4" />)}
      </div>

      {tab === 'kanban' ? (
        <DemandasKanban columns={columns} onMove={moveCard} onOpen={(d) => void openDetail(d)} />
      ) : null}

      {tab === 'lista' ? (
        <Card>
          <CardHeader>
            <CardTitle>Lista · {total} registro(s)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-2">Protocolo</th>
                  <th className="py-2 pr-2">Título</th>
                  <th className="py-2 pr-2">Tipo</th>
                  <th className="py-2 pr-2">Empresa</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Prioridade</th>
                  <th className="py-2 pr-2">Solicitante</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-muted/40">
                    <td className="py-2 pr-2 font-mono text-xs">
                      {d.protocolo}
                      {(d.conversation?.operatorUnreadCount ?? 0) > 0 ? (
                        <Badge variant="danger" className="ml-1">
                          {d.conversation!.operatorUnreadCount}
                        </Badge>
                      ) : null}
                    </td>
                    <td className="py-2 pr-2">{d.titulo}</td>
                    <td className="py-2 pr-2">
                      <Badge variant={tipoVariant(d.tipo)}>{TIPO_LABELS[d.tipo] || d.tipo}</Badge>
                    </td>
                    <td className="py-2 pr-2">{d.company?.name || '—'}</td>
                    <td className="py-2 pr-2">
                      <Badge variant={statusVariant(d.status)}>
                        {STATUS_LABELS[d.status] || d.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2">{d.prioridade}</td>
                    <td className="py-2 pr-2">{d.solicitanteNome || '—'}</td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => void openDetail(d)}>
                        Abrir
                      </Button>
                    </td>
                  </tr>
                ))}
                {!items.length && !loading ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      Nenhuma demanda encontrada.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      {tab === 'chats' ? <DemandasChatPanel companyId={companyId || undefined} /> : null}

      <DemandaCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultCompanyId={companyId || undefined}
        onCreated={(d) => {
          void refresh();
          void openDetail(d);
        }}
      />

      <Modal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setClienteResumo(null);
          setClienteResumoLoading(false);
        }}
        title={selected ? `${selected.protocolo} — ${selected.titulo}` : 'Demanda'}
        size="2xl"
        contentClassName="lg:overflow-hidden !py-3"
      >
        {selected ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:h-[min(calc(90dvh-9rem),38rem)] lg:overflow-hidden">
            <div className="lg:min-h-0 lg:max-h-full lg:overflow-y-auto overscroll-contain space-y-3 pr-1">
              <p className="text-sm whitespace-pre-wrap">{selected.descricao}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-muted-foreground">Status</span>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => void patch(selected.id, { status: v })}
                  >
                    {STATUS_OPTS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Tipo</span>
                  <Select
                    value={selected.tipo}
                    onValueChange={(v) => void patch(selected.id, { tipo: v as DemandaTipo })}
                  >
                    {TIPO_OPTS.map((t) => (
                      <option key={t} value={t}>
                        {TIPO_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Empresa: {selected.company?.name || selected.companyId}</div>
                <div>
                  Solicitante: {selected.solicitanteNome || '—'}{' '}
                  {selected.solicitanteEmail ? `(${selected.solicitanteEmail})` : ''}
                </div>
                <div>
                  Modo chat:{' '}
                  {selected.conversation?.mode === 'HUMAN' ? (
                    <span className="inline-flex items-center gap-1">
                      <UserCheck className="w-3 h-3" /> Humano
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Bot className="w-3 h-3" /> IA
                    </span>
                  )}
                </div>
              </div>
              {selected.solicitanteActorType === 'CLIENTE' ? (
                <DemandaClienteResumoCard
                  data={clienteResumo}
                  loading={clienteResumoLoading}
                />
              ) : null}
              {selected.conversationId ? (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void takeover()}>
                    Assumir
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void release()}>
                    Devolver à IA
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col border rounded-md overflow-hidden h-[min(55vh,28rem)] lg:h-full lg:min-h-0">
              <div className="shrink-0 px-3 py-2 border-b text-sm font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Conversa
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2 bg-muted/20">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`text-sm rounded-lg px-3 py-2 max-w-[95%] ${
                      m.role === 'USER'
                        ? 'bg-slate-900 text-white ml-auto'
                        : m.role === 'OPERATOR'
                          ? 'bg-primary/15 border border-primary/30'
                          : m.role === 'SYSTEM'
                            ? 'bg-amber-50 text-amber-900 text-xs mx-auto'
                            : 'bg-white border'
                    }`}
                  >
                    <div className="text-[10px] opacity-60 mb-0.5">{m.role}</div>
                    {selected.conversationId ? (
                      <AtendimentoMediaBubble
                        message={m}
                        conversationId={selected.conversationId}
                      />
                    ) : (
                      m.content
                    )}
                    <AtendimentoMessageMeta
                      createdAt={m.createdAt}
                      role={m.role}
                      readByPeer={m.readByPeer}
                      portal
                    />
                  </div>
                ))}
                {!messages.length ? (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Sem mensagens nesta demanda.
                  </p>
                ) : null}
              </div>
              {selected.conversationId ? (
                <div className="shrink-0 bg-background">
                  <AtendimentoComposer
                    conversationId={selected.conversationId}
                    showQuickActions
                    isCliente={selected.solicitanteActorType === 'CLIENTE'}
                    onSent={reloadMessages}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
